import { Tag } from '@doda/common/src/components/Head/metaTag';
import {
  CANONICAL_URL_NEW_JOB_LIST,
  OGP_IMAGE,
  OGP_SITE,
  OGP_TYPE,
  OGP_URL_NEW_JOB_LIST,
} from '@doda/common/src/constants/properties/arca';
import { FC, useState } from 'react';
import { Button, SectionTitle } from '@doda/io-react/common';
import { Breadcrumb, BreadcrumbItem } from '@doda/io-react/pc';
import { useCountUpdate } from '@doda/common/src/hooks/useCountUp';
import { useAppDispatch, useAppSelector } from '@doda/pc/src/redux/hooks';
import { convertFeaturedJobsDisplayData } from '@doda/common/src/logics/newJobList/convertFeaturedJobsDisplayData';
import {
  FeaturedJobsResponse,
  JobCountDetailResponse,
} from '@doda/api-client/aspida/@types';
import {
  jobCountSelector,
  setAreaList,
  setIndustryLList,
  setJobCount,
  setSearchJobTypeLList,
} from '@doda/common/src/redux/features/jobCount';
import { QUERY_PARAMS } from '@doda/common/src/constants/queryParams';
import { DODA_SITE_URL } from '@doda/common/src/constants/urls';

import { useCmsMemberInfo } from '@doda/common/src/hooks/useCmsMemberInfo';
import { BreadcrumbItemTop } from 'components/BreadcrumbItemTop';
import { useAnalyticsSetup } from '@doda/common/src/hooks/useAnalytics';
import { NewJobListHeader } from '../../components/NewJobListHeader';
import styles from './index.module.scss';
import { PickUpJobList } from '../../components/PickUpJobList';
import { SearchConditionList } from '../../components/SearchConditionList';
import { MailMagazineLink } from '../../components/MailMagazineLink';
import { initialCheckedList } from '../../logics/clickCheckbox';
import { searchActionToJobSearchList } from '../../logics/clickSearch';

/**
 * 新着求人画面のプロパティを定義するインターフェース
 */
export interface NewJobListScreenProps {
  /** 今週の注目求人 */
  featuredJobs: FeaturedJobsResponse;
  /** 画面上部の新着求人件数 */
  totalJobCount: number;
  /** 条件をクリア押下時にセットするマスタリストの初期件数（職種） */
  occupationInitialCountList: JobCountDetailResponse[];
  /** 条件をクリア押下時にセットするマスタリストの初期件数（地域） */
  areaInitialCountList: JobCountDetailResponse[];
  /** 条件をクリア押下時にセットするマスタリストの初期件数（業種） */
  industryInitialCountList: JobCountDetailResponse[];
}

/**
 * 新着求人画面のコンポーネント
 *
 * @param {NewJobListScreenProps} props - 新着求人画面のプロパティ
 * @returns {JSX.Element} 新着求人画面のJSX要素
 */
export const NewJobListScreen: FC<NewJobListScreenProps> = ({
  featuredJobs,
  totalJobCount,
  occupationInitialCountList,
  areaInitialCountList,
  industryInitialCountList,
}) => {
  const dispatch = useAppDispatch();

  useAnalyticsSetup();
  useCmsMemberInfo('pcHeader');

  const jobCount = useAppSelector(jobCountSelector);
  const countUpRef = useCountUpdate(jobCount?.jobCount ?? 0);

  // 新着フラグがtrueのURLパラメータ（op=1）
  const newJobListFlagParam = QUERY_PARAMS.op.concat('=1');

  // 注目求人の取得、表示データの生成
  const featuredJobDisplayData = convertFeaturedJobsDisplayData(
    featuredJobs?.jobs ?? []
  );

  // チェックボックスの状態
  const [checkedList, setCheckedList] = useState(initialCheckedList);

  // この条件で検索するボタン押下時の処理
  const clickSearch = () => {
    dispatch(searchActionToJobSearchList(checkedList));
  };

  // 条件をクリア押下時の処理
  const clickClear = () => {
    setCheckedList(initialCheckedList);
    dispatch(setJobCount(totalJobCount));
    dispatch(setSearchJobTypeLList(occupationInitialCountList));
    dispatch(setAreaList(areaInitialCountList));
    dispatch(setIndustryLList(industryInitialCountList));
  };

  return (
    <>
      <Tag
        title="新着求人情報 - 転職ならdoda（デューダ）"
        description="dodaの新着・更新求人情報。大手・優良企業の新着求人を毎週更新！あなたの転職に最適な求人はdoda（デューダ）で検索。"
        keywords="新着,転職,求人,求人情報"
        type={OGP_TYPE}
        url={OGP_URL_NEW_JOB_LIST}
        image={OGP_IMAGE}
        siteName={OGP_SITE}
        canonical={CANONICAL_URL_NEW_JOB_LIST}
        robots=""
      />
      <Breadcrumb>
        <BreadcrumbItemTop />
        <BreadcrumbItem>今週の新着・更新求人</BreadcrumbItem>
      </Breadcrumb>
      <NewJobListHeader
        jobCountUpdateDate={jobCount.updateDate ?? ''}
        jobCount={totalJobCount ?? 0}
      />
      <div className={styles.newJobList__body}>
        {featuredJobDisplayData?.length > 0 && (
          <div className={styles.pickupJobList}>
            <div className={styles.section__title}>
              <SectionTitle size="XL" bgType="none">
                今週の注目求人
              </SectionTitle>
            </div>
            <PickUpJobList featuredJobList={featuredJobDisplayData} />
          </div>
        )}
        <Button
          className={styles.allNewJobList__button}
          size="L"
          variant="green"
          as="a"
          href={DODA_SITE_URL.jobSearchList.concat('?', newJobListFlagParam)}
        >
          すべての新着・更新求人を見る
        </Button>
      </div>
      <MailMagazineLink />
      <div className={styles.searchNewJobList__body}>
        <div className={styles.searchConditionList}>
          <div className={styles.section__title}>
            <SectionTitle size="XL" bgType="none">
              希望する条件で新着・更新求人を探す
            </SectionTitle>
          </div>
          <SearchConditionList
            checkedList={checkedList}
            setCheckedList={setCheckedList}
          />
        </div>
        <div className={styles.buttonWrapper}>
          <div className={styles.newJobList__searchButtonAreaJobCount}>
            該当求人
            <span
              className={styles.newJobList__searchButtonAreaJobCountNumber}
              ref={countUpRef}
            />
            件
          </div>
          <Button size="L" variant="green" as="button" onClick={clickSearch}>
            この条件で検索する
          </Button>
          <button
            type="button"
            className={styles.textLink}
            onClick={clickClear}
          >
            条件をクリア
          </button>
        </div>
      </div>
      <MailMagazineLink />
    </>
  );
};
